import { PaperPanel } from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";
import { viewCaption } from "@views/viewIdentity";
import type { View } from "@views/viewsClient";
import type { ReactNode } from "react";

export const ViewCard = ({
  view,
  onClick,
  badge,
  withSubject = false,
}: {
  view: View;
  onClick?: () => void;
  badge?: ReactNode;
  /** Name the patient in the caption. False inside a subject's own pane, where
      every card is already that subject's (BE-2395). */
  withSubject?: boolean;
}) => {
  const caption = viewCaption(view, { withSubject });

  // The compact echo of the reader sheet's masthead: display-face name over
  // mono small print, so the card in the drawer matches the document it opens.
  // The title alone repeats across a subject's cards, so the caption between it
  // and the date is what makes a row identifiable.
  const written = (
    <>
      <span className="flex items-start justify-between gap-2">
        <span className="line-clamp-2 min-w-0 flex-1 font-display font-medium text-ink">
          {view.name}
        </span>
        {badge && <span className="shrink-0">{badge}</span>}
      </span>
      {caption && (
        <span className="mt-1 line-clamp-1 block text-sm text-ink-body">
          {caption}
        </span>
      )}
      <time className="mt-2 block font-mono text-fine-print text-annotation">
        {formatDateTime(view.updatedAt)}
      </time>
    </>
  );

  if (!onClick)
    return (
      <PaperPanel className="p-3" data-testid={`view-card-${view.id}`}>
        {written}
      </PaperPanel>
    );

  return (
    <PaperPanel
      className="p-0 transition duration-150 ease-paper hover:-translate-y-px hover:shadow-lift-lg"
      data-testid={`view-card-${view.id}`}
    >
      {/* The whole card is the click target: a `<button>` inside carries the
          click and keyboard behaviour natively, restating the surface's own
          padding since a button resets it. */}
      <button
        type="button"
        onClick={onClick}
        // Titles repeat across a subject's cards, so the caption is what makes
        // this distinguishable to a screen reader reading the list.
        aria-label={`Open view ${[view.name, caption].filter(Boolean).join(", ")}`}
        className="flex w-full flex-col p-3 text-left font-body text-ink focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent"
      >
        {written}
      </button>
    </PaperPanel>
  );
};
